import { useState, useEffect, useCallback } from 'react';
import { ref, set, get, remove, update } from 'firebase/database';
import { database } from '../firebase/firebase';
import { useAuth } from './useAuth';

interface EventHistoryItem {
    joinedAt: number;
    eventName: string;
    eventDescription: string;
    participantCount?: number;
    createdAt?: number;
}

interface EventHistoryData {
    [eventId: string]: EventHistoryItem;
}

const LOCAL_STORAGE_KEY = 'eventHistory';
const MERGED_FLAG_PREFIX = 'eventHistoryMerged:';

// Re-record a visit at most this often, so revisiting an event still bumps it
// to the top of "recent" without writing on every render.
const REVISIT_THROTTLE_MS = 5 * 60 * 1000;

// Always read localStorage fresh: React state can be a step behind (or empty
// while it is still loading), and writing stale state back loses entries.
const readLocalHistory = (): EventHistoryData => {
    try {
        return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '{}') || {};
    } catch (err) {
        console.error('Error parsing local history:', err);
        return {};
    }
};

const writeLocalHistory = (data: EventHistoryData) => {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
    } catch (err) {
        console.error('Error saving local history:', err);
    }
};

// Firebase rejects any payload containing undefined
const stripUndefined = (item: EventHistoryItem): EventHistoryItem => (
    Object.fromEntries(
        Object.entries(item).filter(([, value]) => value !== undefined)
    ) as unknown as EventHistoryItem
);

/** Whether this device has opened any event, signed in or not. */
export const hasLocalHistory = (): boolean => Object.keys(readLocalHistory()).length > 0;

/**
 * Events visited before signing in only ever reached localStorage. Carries them
 * into the account once, without resurrecting anything the user has since
 * deleted from it, and returns the account's history with them included.
 *
 * Shared by the event page and My Events: signing in from the home page, or
 * from My Events itself, reaches the list before any event page has run this.
 */
export const mergeLocalHistory = async (uid: string, remoteHistory: EventHistoryData): Promise<EventHistoryData> => {
    const mergedFlag = `${MERGED_FLAG_PREFIX}${uid}`;
    if (localStorage.getItem(mergedFlag)) return remoteHistory;

    const missing: EventHistoryData = {};
    Object.entries(readLocalHistory()).forEach(([eventId, item]) => {
        if (!remoteHistory[eventId]) {
            missing[eventId] = stripUndefined(item);
        }
    });

    if (Object.keys(missing).length > 0) {
        try {
            await update(ref(database, `userEventHistory/${uid}`), missing);
        } catch (err) {
            // Left unflagged, so the next load tries again. The account's own
            // history is still worth showing — My Events would be empty otherwise.
            console.error('Error carrying local history over:', err);
            return remoteHistory;
        }
    }
    localStorage.setItem(mergedFlag, '1');
    return { ...remoteHistory, ...missing };
};

export const useEventHistory = () => {
    const { user, loading: authLoading } = useAuth();
    const [history, setHistory] = useState<EventHistoryData>({});
    const [loading, setLoading] = useState(true);

    const loadHistory = useCallback(async () => {
        // Firebase restores the session asynchronously. Loading before it settles
        // would read the anonymous history and, worse, let a write built on that
        // empty state overwrite the signed-in user's history.
        if (authLoading) return;

        setLoading(true);
        try {
            if (user) {
                const snapshot = await get(ref(database, `userEventHistory/${user.uid}`));
                setHistory(await mergeLocalHistory(user.uid, snapshot.val() || {}));
            } else {
                setHistory(readLocalHistory());
            }
        } catch (error) {
            console.error('Error loading history:', error);
            setHistory({});
        } finally {
            setLoading(false);
        }
    }, [user, authLoading]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const addToHistory = useCallback(async (eventId: string, eventName: string, eventDescription: string = '', participantCount?: number, createdAt?: number) => {
        if (!eventId || !eventName) return;

        // Don't record the visit until we know who is visiting, otherwise a
        // signed-in user's visit is saved to localStorage only.
        if (authLoading) return;

        const existing = history[eventId];
        if (existing
            && existing.eventName === eventName
            && Date.now() - existing.joinedAt < REVISIT_THROTTLE_MS) {
            return;
        }

        const historyItem = stripUndefined({
            joinedAt: Date.now(),
            eventName,
            eventDescription,
            participantCount,
            createdAt
        });

        try {
            if (user) {
                // Write just this event. Writing the whole node from local state
                // used to wipe every other event whenever that state was stale.
                await set(ref(database, `userEventHistory/${user.uid}/${eventId}`), historyItem);
            }

            // Always keep a local copy as well, for signed-out visits
            writeLocalHistory({ ...readLocalHistory(), [eventId]: historyItem });
            setHistory(prev => ({ ...prev, [eventId]: historyItem }));
        } catch (error) {
            console.error('Error adding to history:', error);
        }
    }, [user, authLoading, history]);

    const removeFromHistory = useCallback(async (eventId: string) => {
        if (!eventId) return;

        try {
            if (user) {
                await remove(ref(database, `userEventHistory/${user.uid}/${eventId}`));
            }

            const localHistory = readLocalHistory();
            delete localHistory[eventId];
            writeLocalHistory(localHistory);

            setHistory(prev => {
                const next = { ...prev };
                delete next[eventId];
                return next;
            });
        } catch (error) {
            console.error('Error removing from history:', error);
        }
    }, [user]);

    const clearHistory = useCallback(async () => {
        try {
            if (user) {
                await remove(ref(database, `userEventHistory/${user.uid}`));
            }
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            setHistory({});
        } catch (error) {
            console.error('Error clearing history:', error);
        }
    }, [user]);

    const isEventInHistory = useCallback((eventId: string) => {
        return eventId in history;
    }, [history]);

    const getHistoryCount = useCallback(() => {
        return Object.keys(history).length;
    }, [history]);

    const getHistoryArray = useCallback(() => {
        return Object.entries(history)
            .map(([eventId, item]) => ({ eventId, ...item }))
            .sort((a, b) => b.joinedAt - a.joinedAt);
    }, [history]);

    return {
        history,
        loading,
        addToHistory,
        removeFromHistory,
        clearHistory,
        isEventInHistory,
        getHistoryCount,
        getHistoryArray,
        refresh: loadHistory
    };
};
